import {Languages} from 'lucide-react';
import {Button, Spinner} from '@kroll-resolver-crisp/ui-components-react';
import {useTranslationStore} from '@/store/translationStore';
import {cn} from '@/lib/utils';

/**
 The one "Translate" control every host site renders - keyed by an analyst-facing item key so a
 single click flips every translatable field sharing that key (title, description, tags) at once,
 regardless of which component actually owns each field's markup. See useTranslatedText for the
 read side.
 */
export function TranslateToggle({
  itemKey,
  texts,
  className,
  inline = false
}: {
  readonly itemKey: string;
  readonly texts: Array<string | null | undefined>;
  readonly className?: string;
  // A bare lower-case link that follows a language name ("French, translate"), not a standalone
  // button - see ItemLanguage.
  readonly inline?: boolean;
}) {
  const isShown = useTranslationStore((s) => s.shownFor.has(itemKey));
  const isPending = useTranslationStore((s) => s.pendingFor.has(itemKey));
  const error = useTranslationStore((s) => s.errorFor.get(itemKey));
  const translate = useTranslationStore((s) => s.translate);
  const hide = useTranslationStore((s) => s.hide);

  const translatableTexts = texts.filter((t): t is string => Boolean(t?.trim()));
  if (translatableTexts.length === 0) {
    return null;
  }

  return (
    <div className={cn(inline && 'inline-flex items-center gap-1', className)}>
      <Button
        type="button"
        variant={inline ? 'link' : 'ghost'}
        size="xs"
        disabled={isPending}
        className={
          inline ? 'text-primary-ink h-auto p-0 text-[11px]' : 'text-muted-foreground -ml-2'
        }
        onClick={(event) => {
          // Every host renders this inside a clickable card/row - without this, translating would
          // also trigger whatever the card's own click does (e.g. open ClassifyDialog).
          event.stopPropagation();
          if (isShown) {
            hide(itemKey);
          } else {
            void translate(itemKey, translatableTexts);
          }
        }}>
        {isPending ? (
          <Spinner />
        ) : inline ? null : (
          <Languages className={cn(isShown && 'text-primary-ink')} />
        )}
        {isShown
          ? inline
            ? 'show original'
            : 'Show original'
          : inline
            ? 'translate'
            : 'Translate'}
      </Button>
      {error ? <p className="text-destructive-ink text-xs">{error}</p> : null}
    </div>
  );
}
